import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "./ui/drawer";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Check,
  ChevronDown,
  Plus,
  KeyRound,
  Package,
  Users as UsersIcon,
  Crown,
  Tag,
} from "lucide-react";

/**
 * PantrySwitcher — dashboard header chip showing the active pantry name with
 * a click-to-open drawer listing every household the user belongs to plus
 * inline forms to create a new pantry or join one with an invite code.
 */
export default function PantrySwitcher() {
  const { user, refresh } = useAuth();
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [active, setActive] = useState(user?.household_id || null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("list"); // list | create | join
  const [newName, setNewName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const lastFetchRef = useRef(0);

  const load = async () => {
    try {
      const r = await api.get("/households/list");
      setList(r.data.households || []);
      setActive(r.data.active_household_id || null);
      lastFetchRef.current = Date.now();
    } catch (_e) { /* noop */ }
  };

  useEffect(() => {
    if (!open) return;
    setTab("list");
    setNewName("");
    setJoinCode("");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Always refresh once on mount so the chip shows the latest active pantry name.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.household_id]);

  const activePantry = list.find((h) => h.household_id === active) || null;
  const activeName = activePantry?.name || (user?.household_id ? "…" : "—");

  const handleErr = (e, fallback) => {
    const detail = e?.response?.data?.detail || "";
    const m = /^plan_limit:([a-z_]+):(\d+)$/.exec(detail);
    if (m) {
      const key = m[1] === "max_households" ? "plan.limit.households" : m[1] === "max_members" ? "plan.limit.members" : null;
      if (key) { toast.error(t(key, { n: m[2] })); return; }
    }
    toast.error(detail || fallback);
  };

  const switchTo = async (id) => {
    if (id === active) { setOpen(false); return; }
    setBusy(true);
    try {
      await api.post("/households/switch", { household_id: id });
      await refresh();
      setActive(id);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  const create = async () => {
    if (!newName.trim()) { toast.error(t("common.required")); return; }
    setBusy(true);
    try {
      await api.post("/households", { name: newName.trim() });
      await refresh();
      toast.success(t("onb.toast.created"));
      setOpen(false);
    } catch (e) {
      handleErr(e, t("common.error"));
    } finally { setBusy(false); }
  };

  const join = async () => {
    if (!joinCode.trim()) { toast.error(t("common.required")); return; }
    setBusy(true);
    try {
      await api.post("/households/join", { invite_code: joinCode.trim().toUpperCase() });
      await refresh();
      toast.success(t("onb.toast.joined"));
      setOpen(false);
    } catch (e) {
      handleErr(e, t("onb.toast.invalid_code"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 max-w-[60vw] h-8 pl-2 pr-2.5 rounded-full bg-zinc-100 hover:bg-zinc-200 active:scale-95 transition"
        data-testid="pantry-switcher-btn"
      >
        <Tag className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
        <span className="text-xs font-semibold text-zinc-900 truncate">{activeName}</span>
        <ChevronDown className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
      </button>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent data-testid="pantry-switcher-drawer">
          <div className="mx-auto w-full max-w-md text-zinc-950">
            <DrawerHeader className="text-left">
              <DrawerTitle className="font-heading text-2xl font-medium">{t("switcher.title")}</DrawerTitle>
              <DrawerDescription>{t("switcher.subtitle")}</DrawerDescription>
            </DrawerHeader>

            {/* Tabs */}
            <div className="px-4 grid grid-cols-3 bg-zinc-100 rounded-xl p-1 h-11">
              {[
                { v: "list", label: t("switcher.tab.mine") },
                { v: "create", label: t("switcher.tab.create") },
                { v: "join", label: t("switcher.tab.join") },
              ].map(({ v, label }) => (
                <button
                  key={v}
                  onClick={() => setTab(v)}
                  className={`rounded-lg text-xs font-semibold transition ${tab === v ? "bg-white shadow-sm text-zinc-950" : "text-zinc-500"}`}
                  data-testid={`switcher-tab-${v}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="px-4 pt-4 pb-2 max-h-[55vh] overflow-y-auto">
              {tab === "list" && (
                <div className="space-y-2" data-testid="switcher-list">
                  {list.length === 0 && (
                    <div className="text-sm text-zinc-500 text-center py-6">{t("switcher.empty")}</div>
                  )}
                  {list.map((h) => {
                    const isActive = h.household_id === active;
                    return (
                      <button
                        key={h.household_id}
                        onClick={() => switchTo(h.household_id)}
                        disabled={busy}
                        className={`w-full text-left rounded-2xl border p-3 flex items-center gap-3 transition active:scale-[0.99] ${isActive ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white"}`}
                        data-testid={`switcher-item-${h.household_id}`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? "bg-fresh-green text-black" : "bg-zinc-100 text-zinc-700"}`}>
                          {h.is_owner ? <Crown className="w-4 h-4" /> : <UsersIcon className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-heading text-base font-medium truncate">{h.name}</div>
                          <div className={`text-[11px] mt-0.5 flex items-center gap-2 ${isActive ? "text-white/70" : "text-zinc-500"}`}>
                            <span className="font-mono">{h.invite_code}</span>
                            <span>·</span>
                            <span className="flex items-center gap-1"><Package className="w-3 h-3" />{h.items_count}</span>
                            <span>·</span>
                            <span className="flex items-center gap-1"><UsersIcon className="w-3 h-3" />{h.members_count}</span>
                          </div>
                        </div>
                        {isActive && <Check className="w-4 h-4" strokeWidth={3} />}
                      </button>
                    );
                  })}
                </div>
              )}

              {tab === "create" && (
                <div className="space-y-3" data-testid="switcher-create-form">
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{t("onb.create.label")}</label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder={t("onb.create.placeholder")}
                    className="h-12 rounded-xl bg-zinc-50 border-zinc-200"
                    data-testid="switcher-create-name"
                  />
                </div>
              )}

              {tab === "join" && (
                <div className="space-y-3" data-testid="switcher-join-form">
                  <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">{t("onb.join.label")}</label>
                  <div className="relative">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8))}
                      placeholder={t("onb.join.placeholder")}
                      maxLength={8}
                      className="h-12 pl-11 rounded-xl text-lg font-mono tracking-[0.3em] text-center bg-zinc-50 border-zinc-200"
                      data-testid="switcher-join-code"
                    />
                  </div>
                </div>
              )}
            </div>

            <DrawerFooter className="flex-row gap-3">
              {tab === "create" && (
                <Button onClick={create} disabled={busy} className="flex-1 h-12 rounded-xl bg-zinc-950 text-white font-bold" data-testid="switcher-create-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  {busy ? t("common.loading") : t("onb.create.btn")}
                </Button>
              )}
              {tab === "join" && (
                <Button onClick={join} disabled={busy} className="flex-1 h-12 rounded-xl bg-zinc-950 text-white font-bold" data-testid="switcher-join-btn">
                  <KeyRound className="w-4 h-4 mr-1" />
                  {busy ? t("common.loading") : t("onb.join.btn")}
                </Button>
              )}
              {tab === "list" && (
                <Button onClick={() => setOpen(false)} variant="secondary" className="flex-1 h-12 rounded-xl bg-zinc-100 text-zinc-700" data-testid="switcher-close-btn">
                  {t("common.cancel")}
                </Button>
              )}
            </DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
